import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Download,
  HardDrive,
  LayoutGrid,
  Link as LinkIcon,
  List as ListIcon,
  Network,
  Plus,
  Search,
  Sparkles,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TagBadge } from "@/components/ui/TagBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { cn } from "@/lib/utils";
import type { DropdownItem } from "@/modules/dropdowns/types";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { CollectionsSidebar } from "../components/CollectionsSidebar";
import { DocumentTreeView } from "../components/DocumentTreeView";
import { FileCard } from "../components/FileCard";
import { FileTypeIcon } from "../components/FileTypeIcon";
import {
  formatFileSize,
  useAddExternalLink,
  useBulkDeleteMedia,
  useDownloadFile,
  useMediaCollections,
  useMediaList,
  useMediaStats,
  useMediaTags,
  useMoveToCollection,
  useUploadFile,
} from "../hooks/useMedia";
import { mediaNeedsAttention, type MediaFile } from "../types";

const FILE_TYPES = [
  { value: "", label: "All Types" },
  { value: "document", label: "Document" },
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
  { value: "audio", label: "Audio" },
  { value: "spreadsheet", label: "Spreadsheet" },
  { value: "presentation", label: "Presentation" },
  { value: "archive", label: "Archive" },
  { value: "code", label: "Code" },
  { value: "link", label: "Link" },
  { value: "other", label: "Other" },
];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "name_asc", label: "Name A–Z" },
  { value: "size_desc", label: "Size Large–Small" },
  { value: "most_downloaded", label: "Most Downloaded" },
];

const PAGE_SIZES = [5, 10, 25, 50];

function StatCard({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-white p-4">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const days = Math.floor(diff / (24 * 3600 * 1000));
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function MediaVaultPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeCollection, setActiveCollection] = useState("all");
  const [search, setSearch] = useState("");
  const [fileType, setFileType] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [viewMode, setViewMode] = useState<"grid" | "list" | "tree">("grid");
  const [companyFilter, setCompanyFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<
    | { kind: "one"; file: MediaFile }
    | { kind: "bulk"; ids: string[] }
    | null
  >(null);
  const [linkForm, setLinkForm] = useState({ title: "", url: "", description: "" });
  const [linkTags, setLinkTags] = useState<string[]>([]);
  const [linkCollectionId, setLinkCollectionId] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Direct-Vault upload metadata dialog. Picking a file populates this
  // state; the dialog stays open while the user optionally fills in
  // metadata, then Save kicks off the actual upload.
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadMeta, setUploadMeta] = useState<{
    title: string;
    description: string;
    tags: string[];
    companyIds: string[];
    productIds: string[];
    projectIds: string[];
  }>({
    title: "",
    description: "",
    tags: [],
    companyIds: [],
    productIds: [],
    projectIds: [],
  });

  const entityType = searchParams.get("entityType") ?? undefined;
  const entityId = searchParams.get("entityId") ?? undefined;

  const { data: stats } = useMediaStats();
  const { data: collections } = useMediaCollections();
  const tagOptions = useMediaTags().data ?? [];
  // Linked To picker should list both operational and external companies
  // so users can attach files to investors, partners, etc. The Company
  // *filter* dropdown above also benefits — Documents linked to an
  // external company should be filterable.
  const { data: companiesData } = useCompanyList({ limit: 200 });
  const { data: productsData } = useProductList({
    limit: 100,
    companyId: companyFilter || undefined,
  });
  const { data: projectsData } = useProjectList({
    limit: 100,
    companyId: companyFilter || undefined,
    productId: productFilter || undefined,
  });

  const collectionId = activeCollection.startsWith("col:")
    ? activeCollection.slice(4)
    : undefined;
  const myFiles = activeCollection === "mine";
  const recentOnly = activeCollection === "recent";

  const listParams = useMemo(() => {
    const p: Parameters<typeof useMediaList>[0] = {
      page,
      limit,
      search: search || undefined,
      fileType: fileType || undefined,
      sortBy,
      collectionId,
      entityType,
      entityId,
      companyId: companyFilter || undefined,
      productId: productFilter || undefined,
      projectId: projectFilter || undefined,
      dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
      dateTo: dateTo
        ? new Date(
            new Date(dateTo).setHours(23, 59, 59, 999),
          ).toISOString()
        : undefined,
    };
    if (myFiles && user) p.uploadedById = user.id;
    if (recentOnly && !p.dateFrom) {
      p.dateFrom = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
    }
    return p;
  }, [
    page,
    limit,
    search,
    fileType,
    sortBy,
    collectionId,
    entityType,
    entityId,
    companyFilter,
    productFilter,
    projectFilter,
    dateFrom,
    dateTo,
    myFiles,
    user,
    recentOnly,
  ]);

  const { data, isLoading } = useMediaList(listParams);
  const uploader = useUploadFile();
  const addLink = useAddExternalLink();
  const bulkDelete = useBulkDeleteMedia();
  const downloadMutation = useDownloadFile();
  const moveToCollection = useMoveToCollection();

  useEffect(() => {
    setPage(1);
  }, [
    activeCollection,
    search,
    fileType,
    sortBy,
    entityType,
    entityId,
    companyFilter,
    productFilter,
    projectFilter,
    dateFrom,
    dateTo,
  ]);

  useEffect(() => {
    setProductFilter("");
    setProjectFilter("");
  }, [companyFilter]);

  useEffect(() => {
    setProjectFilter("");
  }, [productFilter]);

  function resetFilters() {
    setSearch("");
    setFileType("");
    setSortBy("newest");
    setCompanyFilter("");
    setProductFilter("");
    setProjectFilter("");
    setDateFrom("");
    setDateTo("");
  }

  const filtersActive =
    !!search ||
    !!fileType ||
    !!companyFilter ||
    !!productFilter ||
    !!projectFilter ||
    !!dateFrom ||
    !!dateTo ||
    sortBy !== "newest";

  const files = data?.data ?? [];
  const totalFiles = stats?.totalFiles ?? 0;
  const totalSize = stats?.totalSize ?? 0;
  const recentUploads = stats?.recentUploads ?? 0;
  const collectionCount = collections?.length ?? 0;

  function toggleSelect(file: MediaFile) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(file.id)) next.delete(file.id);
      else next.add(file.id);
      return next;
    });
  }

  function clearSelection() {
    setSelected(new Set());
  }

  // Picking a file in the Vault context queues it for the metadata
  // dialog rather than uploading instantly. Multiple files share one
  // metadata form; per-file title defaults to filename if left blank.
  function onFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const list = Array.from(files);
    setPendingFiles(list);
    setUploadMeta({
      title: list.length === 1 ? list[0].name : "",
      description: "",
      tags: [],
      companyIds: [],
      productIds: [],
      projectIds: [],
    });
    setUploadDialogOpen(true);
    e.target.value = "";
  }

  async function submitUpload() {
    if (pendingFiles.length === 0) return;
    for (const f of pendingFiles) {
      await uploader.mutate({
        file: f,
        title: uploadMeta.title.trim() || f.name,
        description: uploadMeta.description.trim() || undefined,
        tags: uploadMeta.tags.length > 0 ? uploadMeta.tags : undefined,
        companyIds:
          uploadMeta.companyIds.length > 0
            ? uploadMeta.companyIds
            : undefined,
        productIds:
          uploadMeta.productIds.length > 0
            ? uploadMeta.productIds
            : undefined,
        projectIds:
          uploadMeta.projectIds.length > 0
            ? uploadMeta.projectIds
            : undefined,
        entityType,
        entityId,
        collectionId,
      });
    }
    setPendingFiles([]);
    setUploadDialogOpen(false);
  }

  function cancelUpload() {
    setPendingFiles([]);
    setUploadDialogOpen(false);
  }

  async function submitLink(e: React.FormEvent) {
    e.preventDefault();
    if (!linkForm.title.trim() || !linkForm.url.trim()) return;
    await addLink.mutateAsync({
      title: linkForm.title.trim(),
      externalUrl: linkForm.url.trim(),
      description: linkForm.description.trim() || undefined,
      tags: linkTags.length > 0 ? linkTags : undefined,
      collectionId: linkCollectionId || collectionId,
      entityType,
      entityId,
    });
    setLinkForm({ title: "", url: "", description: "" });
    setLinkTags([]);
    setLinkCollectionId("");
    setLinkModalOpen(false);
  }

  async function bulkDownload() {
    for (const id of selected) {
      const file = files.find((f) => f.id === id);
      if (!file) continue;
      await downloadMutation.mutateAsync({
        id: file.id,
        originalName: file.originalName,
        isExternal: file.isExternal,
        externalUrl: file.externalUrl,
      });
    }
  }

  function bulkDeleteSelected() {
    if (selected.size === 0) return;
    setDeleteTarget({ kind: "bulk", ids: Array.from(selected) });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    if (deleteTarget.kind === "one") {
      await bulkDelete.mutateAsync([deleteTarget.file.id]);
    } else {
      await bulkDelete.mutateAsync(deleteTarget.ids);
      clearSelection();
    }
  }

  async function bulkMoveToCollection(targetId: string | null) {
    await moveToCollection.mutateAsync({
      ids: Array.from(selected),
      collectionId: targetId,
    });
    clearSelection();
    setMoveModalOpen(false);
  }

  const totalPages = data?.pagination.totalPages ?? 1;
  const startItem = data && data.data.length > 0 ? (page - 1) * limit + 1 : 0;
  const endItem = data ? (page - 1) * limit + data.data.length : 0;

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub" },
        { label: "Resources" },
        { label: "Media Vault" },
      ]}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={onFileInputChange}
      />

      <div className="flex items-center gap-2 mb-4">
        <HardDrive size={22} className="text-primary" />
        <h1 className="text-2xl font-semibold">Media Vault</h1>
      </div>

      <div className="flex gap-4 items-start">
        <CollectionsSidebar
          collections={collections ?? []}
          activeKey={activeCollection}
          onSelect={setActiveCollection}
          totalFiles={totalFiles}
          recentCount={recentUploads}
        />

        <div className="flex-1 flex flex-col min-w-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <StatCard label="Total Files" value={totalFiles} />
            <StatCard label="Storage Used" value={formatFileSize(totalSize)} />
            <StatCard label="This Week" value={recentUploads} />
            <StatCard label="Collections" value={collectionCount} />
          </div>

          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-[320px]">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                placeholder="Search files..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>

            <select
              value={fileType}
              onChange={(e) => setFileType(e.target.value)}
              className="h-9 rounded-md border bg-white px-2 text-sm"
            >
              {FILE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="h-9 rounded-md border bg-white px-2 text-sm"
            >
              {SORT_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>

            <div className="flex rounded-md border bg-white overflow-hidden">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-9 w-9 flex items-center justify-center",
                  viewMode === "grid"
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-muted",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid size={14} strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-9 w-9 flex items-center justify-center border-l",
                  viewMode === "list"
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-muted",
                )}
                aria-label="List view"
              >
                <ListIcon size={14} strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("tree")}
                className={cn(
                  "h-9 w-9 flex items-center justify-center border-l",
                  viewMode === "tree"
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-muted",
                )}
                aria-label="Tree view"
                title="Tree"
              >
                <Network size={14} strokeWidth={1.5} />
              </button>
            </div>

            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={14} /> Upload
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setLinkModalOpen(true)}
            >
              <LinkIcon size={14} /> Add Link
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-2 mb-3">
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="h-8 rounded-md border bg-white px-2 text-[13px]"
            >
              <option value="">All Companies</option>
              {(companiesData?.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            <select
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              className="h-8 rounded-md border bg-white px-2 text-[13px]"
            >
              <option value="">All Products</option>
              {(productsData?.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>

            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className="h-8 rounded-md border bg-white px-2 text-[13px]"
            >
              <option value="">All Projects</option>
              {(projectsData?.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>

            <span className="text-[12px] text-muted-foreground ml-1">From</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-8 rounded-md border bg-white px-2 text-[13px]"
            />
            <span className="text-[12px] text-muted-foreground">To</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-8 rounded-md border bg-white px-2 text-[13px]"
            />

            {filtersActive && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1 h-8 px-2 rounded-md border bg-white text-[13px] text-muted-foreground hover:bg-muted"
                aria-label="Reset filters"
              >
                <X size={12} /> Reset
              </button>
            )}
          </div>

          {selected.size > 0 && (
            <div className="flex items-center gap-2 mb-3 rounded-md border bg-blue-50 px-3 py-2 text-sm">
              <span className="font-semibold">{selected.size} selected</span>
              <div className="flex-1" />
              <Button variant="outline" size="sm" onClick={bulkDownload}>
                <Download size={12} /> Download
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMoveModalOpen(true)}
              >
                Move to Collection
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={bulkDeleteSelected}
              >
                <Trash2 size={12} /> Delete
              </Button>
              <Button variant="ghost" size="sm" onClick={clearSelection}>
                <X size={12} /> Clear
              </Button>
            </div>
          )}

          {viewMode === "tree" ? (
            <DocumentTreeView />
          ) : (
          <div
            style={{
              background: "white",
              border: "1px solid hsl(210,30%,85%)",
              borderRadius: "0.75rem",
            }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center h-40 text-sm text-muted-foreground">
                Loading files…
              </div>
            ) : files.length === 0 ? (
              <EmptyState onUpload={() => fileInputRef.current?.click()} />
            ) : viewMode === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 p-3">
                {files.map((f) => (
                  <FileCard
                    key={f.id}
                    file={f}
                    isSelected={selected.has(f.id)}
                    onSelect={() => toggleSelect(f)}
                    onOpen={(file) => navigate(`/media-vault/${file.id}`)}
                    onDownload={(file) =>
                      downloadMutation.mutate({
                        id: file.id,
                        originalName: file.originalName,
                        isExternal: file.isExternal,
                        externalUrl: file.externalUrl,
                      })
                    }
                    onDelete={(file) => setDeleteTarget({ kind: "one", file })}
                    onEdit={(file) => navigate(`/media-vault/${file.id}`)}
                    tagOptions={tagOptions}
                  />
                ))}
              </div>
            ) : (
              <FileListTable
                files={files}
                selected={selected}
                onToggle={toggleSelect}
                onOpen={(f) => navigate(`/media-vault/${f.id}`)}
                onDownload={(f) =>
                  downloadMutation.mutate({
                    id: f.id,
                    originalName: f.originalName,
                    isExternal: f.isExternal,
                    externalUrl: f.externalUrl,
                  })
                }
                onDelete={(f) => setDeleteTarget({ kind: "one", file: f })}
                tagOptions={tagOptions}
              />
            )}

            {data && data.pagination.total > 0 && (
              <div data-pagination="true" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '9px 14px',
                borderTop: '1px solid hsl(210,30%,85%)',
                background: 'hsl(210,30%,97%)',
                fontSize: '12px',
                color: 'hsl(215,16%,47%)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>
                    Showing {startItem}-{endItem} of {data.pagination.total} entries
                  </span>
                  <span style={{ marginLeft: '8px' }}>Show</span>
                  <select
                    value={limit}
                    onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                    style={{
                      height: '26px',
                      padding: '0 4px',
                      border: '1px solid hsl(210,30%,85%)',
                      borderRadius: '4px',
                      fontSize: '12px',
                      background: 'white',
                      cursor: 'pointer',
                    }}
                  >
                    {PAGE_SIZES.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <span>entries</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    style={{
                      width: '28px', height: '28px',
                      border: '1px solid hsl(210,30%,85%)',
                      borderRadius: '4px',
                      background: 'white',
                      cursor: page <= 1 ? 'not-allowed' : 'pointer',
                      opacity: page <= 1 ? 0.4 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >‹</button>
                  <span style={{ padding: '0 10px' }}>
                    {totalPages === 0 ? '0' : page} / {totalPages || 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    style={{
                      width: '28px', height: '28px',
                      border: '1px solid hsl(210,30%,85%)',
                      borderRadius: '4px',
                      background: 'white',
                      cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                      opacity: page >= totalPages ? 0.4 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >›</button>
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      <Dialog open={linkModalOpen} onOpenChange={setLinkModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add External Link</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitLink} className="space-y-3">
            <div>
              <label className="text-xs font-medium">Title</label>
              <Input
                value={linkForm.title}
                onChange={(e) =>
                  setLinkForm((f) => ({ ...f, title: e.target.value }))
                }
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium">URL</label>
              <Input
                type="url"
                value={linkForm.url}
                onChange={(e) =>
                  setLinkForm((f) => ({ ...f, url: e.target.value }))
                }
                required
                placeholder="https://…"
              />
            </div>
            <div>
              <label className="text-xs font-medium">Description</label>
              <textarea
                value={linkForm.description}
                onChange={(e) =>
                  setLinkForm((f) => ({ ...f, description: e.target.value }))
                }
                rows={2}
                className="mt-1 w-full rounded border px-2 py-1.5 text-sm resize-none"
              />
            </div>
            <div>
              <label className="text-xs font-medium">Collection</label>
              <select
                value={linkCollectionId}
                onChange={(e) => setLinkCollectionId(e.target.value)}
                className="mt-1 w-full rounded border bg-white px-2 py-1.5 text-sm"
              >
                <option value="">No collection</option>
                {(collections ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium">Tags</label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {tagOptions.length === 0 ? (
                  <span className="text-[11px] italic text-muted-foreground">
                    No tag options configured
                  </span>
                ) : (
                  tagOptions.map((opt) => {
                    const selected = linkTags.includes(opt.value);
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() =>
                          setLinkTags((prev) =>
                            prev.includes(opt.value)
                              ? prev.filter((v) => v !== opt.value)
                              : [...prev, opt.value],
                          )
                        }
                        style={{ opacity: selected ? 1 : 0.5 }}
                      >
                        <TagBadge
                          value={opt.value}
                          label={opt.label}
                          size="sm"
                        />
                      </button>
                    );
                  })
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setLinkModalOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={addLink.isPending}>
                Add Link
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={uploadDialogOpen} onOpenChange={(o) => (o ? null : cancelUpload())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pendingFiles.length === 1
                ? `Upload "${pendingFiles[0]?.name ?? ""}"`
                : `Upload ${pendingFiles.length} files`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {pendingFiles.length === 1 && (
              <div>
                <label className="text-xs font-medium">Title</label>
                <Input
                  value={uploadMeta.title}
                  onChange={(e) =>
                    setUploadMeta((m) => ({ ...m, title: e.target.value }))
                  }
                  placeholder={pendingFiles[0]?.name ?? "File title"}
                />
                <div className="mt-1 text-[11px] text-muted-foreground">
                  Defaults to the filename if left blank.
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-medium">Description</label>
              <textarea
                value={uploadMeta.description}
                onChange={(e) =>
                  setUploadMeta((m) => ({ ...m, description: e.target.value }))
                }
                rows={2}
                className="mt-1 w-full rounded border px-2 py-1.5 text-sm resize-none"
                placeholder="Optional"
              />
            </div>

            <div>
              <label className="text-xs font-medium">Tags</label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {tagOptions.length === 0 ? (
                  <span className="text-[11px] italic text-muted-foreground">
                    No tag options configured
                  </span>
                ) : (
                  tagOptions.map((opt) => {
                    const sel = uploadMeta.tags.includes(opt.value);
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() =>
                          setUploadMeta((m) => ({
                            ...m,
                            tags: m.tags.includes(opt.value)
                              ? m.tags.filter((v) => v !== opt.value)
                              : [...m.tags, opt.value],
                          }))
                        }
                        style={{ opacity: sel ? 1 : 0.5 }}
                      >
                        <TagBadge value={opt.value} label={opt.label} size="sm" />
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <MultiPickerField
              label="Linked Companies"
              options={(companiesData?.data ?? []).map((c) => ({
                id: c.id,
                name: c.name,
              }))}
              value={uploadMeta.companyIds}
              onChange={(ids) =>
                setUploadMeta((m) => ({ ...m, companyIds: ids }))
              }
            />
            <MultiPickerField
              label="Linked Products"
              options={(productsData?.data ?? []).map((p) => ({
                id: p.id,
                name: p.name,
              }))}
              value={uploadMeta.productIds}
              onChange={(ids) =>
                setUploadMeta((m) => ({ ...m, productIds: ids }))
              }
            />
            <MultiPickerField
              label="Linked Projects"
              options={(projectsData?.data ?? []).map((p) => ({
                id: p.id,
                name: p.name,
              }))}
              value={uploadMeta.projectIds}
              onChange={(ids) =>
                setUploadMeta((m) => ({ ...m, projectIds: ids }))
              }
            />

            <div className="flex items-center justify-between gap-2 pt-2">
              <Button type="button" variant="outline" onClick={cancelUpload}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={submitUpload}
                disabled={uploader.isUploading}
              >
                {pendingFiles.length === 1
                  ? "Save"
                  : `Save ${pendingFiles.length} files`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={
          deleteTarget?.kind === "bulk" ? "Delete files?" : "Delete file?"
        }
        description={(() => {
          if (!deleteTarget) return "";
          if (deleteTarget.kind === "bulk") {
            const n = deleteTarget.ids.length;
            return `Are you sure you want to delete ${n} selected file${n === 1 ? "" : "s"}? They will be removed from your Media Vault.`;
          }
          return `Are you sure you want to delete '${deleteTarget.file.title}'? It will be removed from your Media Vault.`;
        })()}
        onConfirm={confirmDelete}
      />

      <Dialog open={moveModalOpen} onOpenChange={setMoveModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move to Collection</DialogTitle>
          </DialogHeader>
          <div className="space-y-1">
            <button
              type="button"
              onClick={() => bulkMoveToCollection(null)}
              className="flex w-full items-center justify-between rounded hover:bg-muted px-2 py-1.5 text-sm"
            >
              <span className="italic text-muted-foreground">No collection</span>
            </button>
            {(collections ?? []).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => bulkMoveToCollection(c.id)}
                className="flex w-full items-center justify-between rounded hover:bg-muted px-2 py-1.5 text-sm"
              >
                <span>{c.name}</span>
                <span className="text-[10px] text-muted-foreground">
                  {c.fileCount}
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

/**
 * Lightweight multi-select picker used in upload/edit forms for Linked To
 * Company/Product/Project. Renders the existing options as toggleable
 * chips so the form stays compact and matches the tag-chip pattern used
 * elsewhere in the codebase.
 */
export function MultiPickerField({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<{ id: string; name: string }>;
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  return (
    <div>
      <label className="text-xs font-medium">{label}</label>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {options.length === 0 ? (
          <span className="text-[11px] italic text-muted-foreground">
            None available
          </span>
        ) : (
          options.map((opt) => {
            const sel = value.includes(opt.id);
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() =>
                  onChange(
                    value.includes(opt.id)
                      ? value.filter((v) => v !== opt.id)
                      : [...value, opt.id],
                  )
                }
                className={cn(
                  "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                  sel
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-white text-muted-foreground hover:bg-muted",
                )}
              >
                {opt.name}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function EmptyState({ onUpload }: { onUpload: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-center">
      <HardDrive size={48} className="text-muted-foreground opacity-50" />
      <div className="mt-3 text-lg font-semibold">No files yet</div>
      <div className="mt-1 text-sm text-muted-foreground max-w-sm">
        Upload files or add external links to get started.
      </div>
      <Button className="mt-4" onClick={onUpload}>
        <Plus size={14} /> Upload Your First File
      </Button>
    </div>
  );
}

function FileListTable({
  files,
  selected,
  onToggle,
  onOpen,
  onDownload,
  onDelete,
  tagOptions,
}: {
  files: MediaFile[];
  selected: Set<string>;
  onToggle: (f: MediaFile) => void;
  onOpen: (f: MediaFile) => void;
  onDownload: (f: MediaFile) => void;
  onDelete: (f: MediaFile) => void;
  tagOptions: DropdownItem[];
}) {
  return (
    <div>
      <table className="w-full text-sm">
        <thead className="bg-muted text-[11px] uppercase text-muted-foreground">
          <tr>
            <th className="w-10 px-2 py-2" />
            <th className="px-3 py-2 text-left">File</th>
            <th className="px-3 py-2 text-left">Type</th>
            <th className="px-3 py-2 text-left">Size</th>
            <th className="px-3 py-2 text-left">Collection</th>
            <th
              className="px-3 py-2 text-left"
              style={{ width: "150px" }}
            >
              Tags
            </th>
            <th className="px-3 py-2 text-left">Uploaded By</th>
            <th className="px-3 py-2 text-left">Date</th>
            <th className="w-24 px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {files.map((f) => (
            <tr
              key={f.id}
              className="border-t hover:bg-muted/40 cursor-pointer"
              onClick={() => onOpen(f)}
            >
              <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selected.has(f.id)}
                  onChange={() => onToggle(f)}
                  className="h-4 w-4"
                />
              </td>
              <td className="px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FileTypeIcon fileType={f.fileType} size={24} />
                  <div className="min-w-0">
                    <div className="font-medium truncate flex items-center gap-1.5">
                      {mediaNeedsAttention(f) && (
                        <span
                          className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600"
                          title="Missing details — click to add"
                          aria-label="Missing details"
                        >
                          <Sparkles size={10} strokeWidth={2} />
                        </span>
                      )}
                      <span className="truncate">{f.title}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {f.originalName}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-3 py-2">
                <span className="text-xs rounded bg-muted px-2 py-0.5">
                  {f.fileType}
                </span>
              </td>
              <td className="px-3 py-2 text-xs">
                {formatFileSize(f.fileSize)}
              </td>
              <td className="px-3 py-2 text-xs">{f.collectionName ?? "—"}</td>
              <td className="px-3 py-2" style={{ width: "150px" }}>
                {f.tags.length === 0 ? (
                  <span className="text-xs text-muted-foreground">—</span>
                ) : (
                  <div className="flex flex-wrap items-center gap-1">
                    {f.tags.slice(0, 2).map((t) => {
                      const opt = tagOptions.find((o) => o.value === t);
                      return (
                        <TagBadge
                          key={t}
                          value={t}
                          label={opt?.label ?? t}
                          size="sm"
                        />
                      );
                    })}
                    {f.tags.length > 2 && (
                      <span className="text-[10px] text-muted-foreground">
                        +{f.tags.length - 2}
                      </span>
                    )}
                  </div>
                )}
              </td>
              <td className="px-3 py-2 text-xs">
                {f.uploadedByName ?? "—"}
              </td>
              <td className="px-3 py-2 text-xs">
                {formatRelativeDate(f.createdAt)}
              </td>
              <td
                className="px-3 py-2 text-right"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => onDownload(f)}
                  className="p-1 rounded hover:bg-muted"
                  aria-label="Download"
                >
                  <Download size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(f)}
                  className="p-1 rounded hover:bg-muted text-destructive"
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
